import type { HomeStateData } from '@workspace/api-backend'

export type HomeStateKey = HomeStateData['key']
export type BannerKey = HomeStateData['banners'][number]

/**
 * Home card copy, verbatim from the approved mockup.
 *
 * Lives here rather than in the API because it is presentation: the headline
 * splits so exactly one phrase carries the accent, it changes on a copy review
 * rather than a deploy, and the backend has no other founder-facing prose. What
 * the backend owns is the numbers — every {token} below is filled from the
 * home-state payload, so no date maths happens in the browser.
 *
 * Typed as Record<HomeStateKey, …> against the generated key union, so a state
 * the resolver can return but nobody has written copy for fails the build.
 *
 * Voice rules, enforced by review: no em dashes, no "not X, but Y", never claim
 * a meeting happened, never say "disputable", and no Help Center links (it has
 * no published articles).
 */
export interface CardCopy {
  eyebrow: string
  /** Headline, split so one phrase carries the accent colour. */
  title: string
  titleAccent: string
  why: string
  /** `null` removes the primary button entirely; the ghost becomes the only action. */
  cta: string | null
  ghost: string | null
  /** Quiet money-disclosure line under the actions. */
  pill?: { text: string; linkLabel: string }
  /** "I'll do this later" text link, rendered in the footnote beside `next`.
   *  Distinct from `ghost`, which is a real button. */
  snooze?: string
  next: string
  /**
   * Sidebar button label, when it differs from the primary CTA. Explicit `null`
   * means the state has no next action, so the sidebar shows nothing rather
   * than promoting the card's secondary action.
   */
  menuCta?: string | null
}

export const HOME_COPY: Record<HomeStateKey, CardCopy> = {
  QUEST_UPLOAD_DECK: {
    eyebrow: 'Start here',
    title: 'Get an honest read on your',
    titleAccent: 'startup.',
    why: 'Your deck becomes a SeedForge Profile: your story, numbers and proof in the shape investors expect, with the deck attached. One link you can share with any investor, and the starting point for reaching the ones that match you.',
    cta: 'Upload your deck',
    ghost: null,
    next: 'see the investors you match, and reach them from your own LinkedIn account.',
  },
  QUEST_READING_DECK: {
    eyebrow: 'Give us a few minutes',
    title: 'SeedForge is reading',
    titleAccent: 'your deck.',
    why: 'You do not need to wait here. Your feedback lands under Feedback in the menu, and we email you the moment it is ready.',
    cta: 'Browse the app meanwhile',
    ghost: null,
    next: 'your feedback appears under Feedback, then completing your profile unlocks your matched investors.',
    // No sidebar button. "Browse the app" is an instruction the founder is
    // already following — they are in the app, reading the menu it sits in.
    // The card keeps it, where it reads as reassurance that waiting here is
    // not required.
    menuCta: null,
  },
  QUEST_READ_FAILED: {
    eyebrow: 'Something went wrong',
    title: 'We could not finish',
    titleAccent: 'reading your deck.',
    why: 'This is our failure, and we see it on our side too. Message us and a human picks it up the same day, or upload the deck again, which usually clears it.',
    cta: 'Message support',
    ghost: 'Upload the deck again',
    next: 'your feedback completes and your profile unlocks your matched investors.',
    menuCta: 'Try again',
  },
  QUEST_COMPLETE_PROFILE: {
    eyebrow: 'Your read is ready',
    title: 'Complete your profile to unlock your',
    titleAccent: 'matched investors.',
    why: 'Your profile is what investors explore before a call. At {unlockThreshold} your full matched investor list unlocks, free, with a drafted intro for each partner. The list above is exactly what is left. Your full feedback is waiting under Feedback whenever you want to read it.',
    cta: 'Complete your profile',
    ghost: 'Review your feedback',
    next: 'your matched investors unlock, each with a drafted intro.',
  },
  QUEST_SHARE: {
    eyebrow: 'Ready to share',
    title: 'Send',
    titleAccent: 'your link.',
    why: 'Decks get opened. Profiles get answered. Founders who send their profile link see who reads what and follow up at the right moment, instead of mailing PDFs into silence.',
    cta: 'Copy your link',
    ghost: 'Open Share your Profile',
    snooze: "I'll do this later",
    next: 'the first time an investor opens your link, this step turns green and your visitor log starts filling in.',
  },
  QUEST_FINDING_INVESTORS: {
    eyebrow: 'Profile complete',
    title: 'Finding the investors that',
    titleAccent: 'match you.',
    why: 'This takes a few minutes, and it is worth the wait: the list you get is ranked by fit, and every partner on it comes with a drafted intro written from your profile.',
    cta: null,
    ghost: 'Explore the app meanwhile',
    next: 'your matched investors appear under Outreach, in Matched investors, ready to approve.',
    menuCta: 'Explore the app',
  },
  QUEST_REVIEW_MESSAGES: {
    eyebrow: 'Your matches are ready',
    title: 'Choose your campaign and approve your',
    titleAccent: 'messages.',
    why: 'One campaign goes direct to your matched investors. The other asks founders who already raised from them for a warm intro. You approve the message we send before any of it goes out, and you can edit it.',
    cta: 'Review your messages',
    // The escape back to self-share — see escape-to-share in cta-routes.ts.
    ghost: "I'd rather send it myself",
    next: 'connect LinkedIn to send them, from your own account.',
  },
  QUEST_APPROVE_OUTREACH: {
    // Reached both while the campaign is still being built and once it is
    // running, so the eyebrow cannot claim either. What is true in both is the
    // thing the card is asking for.
    eyebrow: 'Nothing sends without you',
    title: 'Approve who we reach',
    titleAccent: 'next.',
    why: 'Nothing sends until you approve the contact. Approving is the step you repeat to keep the campaign fed, and a batch at a time is enough, and you can decline anyone.',
    cta: 'Approve your contacts',
    ghost: null,
    next: 'your approved contacts start sending, gradually and safely.',
  },
  QUEST_CONNECT_LINKEDIN: {
    // What the founder approved at this point is the message, not the list of
    // people it goes to — contacts are approved later, on the roster. Saying
    // "Approved" flat reads as "your contacts are approved", which they are not.
    eyebrow: 'Messages approved',
    title: 'Send from your',
    titleAccent: 'own account.',
    why: 'Investors reply to founders. Sending from your own account means every message lands as you, and every reply comes straight back to you. Connect it now and approve who we contact when your list is ready.',
    cta: 'Connect LinkedIn',
    // The security explainer is gone: it pointed at a Help Center with nothing
    // published, and the connect dialog already answers the question. The
    // reassurance moved into `why` so it is read rather than clicked. The ghost
    // slot it left behind is now the escape back to self-share.
    ghost: "I'd rather send it myself",
    next: 'you start the campaign and approve the specific contacts we reach.',
  },
  QUEST_PREPARING_LINK: {
    eyebrow: 'Almost there',
    title: 'Preparing your',
    titleAccent: 'sign-in link.',
    why: 'The link comes from LinkedIn and can take a few minutes to arrive. You can leave this page; we will pick up where you left off.',
    cta: null,
    ghost: 'Message support',
    next: 'once connected, your approved messages start sending gradually.',
    // No sidebar button. The link is being prepared and there is nothing for
    // the founder to do; making "Message support" the one action in the menu
    // reads as an alarm in a state where nothing is wrong. The card still
    // offers support for anyone who wants it.
    menuCta: null,
  },
  QUEST_LAUNCH_CAMPAIGN: {
    // Not "everything is ready": launching opens the roster, it does not start
    // sending, and the founder has not chosen a single contact yet. The two
    // things they need to know before clicking are that they still pick who
    // gets contacted, and that the trial means this costs nothing to try.
    eyebrow: 'Setup complete',
    title: 'Launch your',
    titleAccent: 'campaign.',
    why: 'Your profile, your messages and your LinkedIn are set. Launching opens your list, and you approve each contact before anyone hears from you. Nothing sends until you do. Your first month is free, so starting the campaign costs nothing.',
    cta: 'Launch campaign',
    // The escape back to self-share — see escape-to-share in cta-routes.ts.
    ghost: "I'd rather send it myself",
    next: 'you approve the contacts we reach, and sending starts gradually and safely.',
  },
  QUEST_LIVE: {
    eyebrow: 'Your outreach is live',
    title: 'You are reaching',
    titleAccent: 'investors now.',
    why: 'Messages send gradually so LinkedIn keeps you safe and they land in the inbox. While it runs, the best use of your time is strengthening your proof, so your profile is strong the moment an investor replies.',
    cta: 'Run an AI Session',
    ghost: 'Open your campaign',
    next: 'we surface every reply here the moment it lands.',
  },
  QUEST_PAUSED: {
    eyebrow: 'Paused',
    // No date: pause is a channel-flag toggle and prod stores no paused_at.
    title: 'You paused',
    titleAccent: 'sending.',
    // No charging claim: billing is per engagement, and a paused campaign can
    // still bill for a call booked off a message already sent. Saying "nothing
    // is charged" only holds while the founder is inside the free month.
    why: 'Paused means paused. Nothing sends, and nothing is lost. When you resume, sending picks up from where it stopped.',
    cta: 'Resume sending',
    ghost: 'See your replies',
    next: 'sending resumes and replies keep flowing in.',
  },
  QUEST_RECONNECT: {
    // The calm variant. All ten DISCONNECTED rows on the prod extract carry
    // disconnect_requested_at, so this is the common case, not the rare one —
    // and it must never read as an alarm for a choice the founder made.
    eyebrow: 'Ready when you are',
    title: 'Reconnect LinkedIn to',
    titleAccent: 'resume.',
    why: 'You disconnected your LinkedIn, so sending is off and SeedForge is not tracking replies. Your approved messages and matched investors are saved exactly where they stopped. Reconnect whenever you are ready.',
    cta: 'Reconnect LinkedIn',
    // No second button: "See your campaign" pointed at /outreach, which is
    // exactly where Reconnect lands you.
    ghost: null,
    next: 'sending resumes from where it stopped, and reply tracking picks up again.',
  },
  MOMENT_REPLY: {
    eyebrow: 'You have replies',
    title: 'An investor',
    titleAccent: 'replied.',
    why: 'This is the moment the whole engine is built for. Investors reply right in the chat, so offer your availability there. When a founder offers a warm intro, that is a real door opened. Reply while it is warm.',
    cta: 'Reply now',
    ghost: null,
    pill: {
      text: 'You only pay when it lands. Full terms on your',
      linkLabel: 'Wallet pricing page',
    },
    next: 'when they book a call or offer the intro, that is your first outcome.',
  },
  MOMENT_OUTCOME_VERIFYING: {
    eyebrow: 'Looks promising',
    title: 'It looks like a call is being',
    titleAccent: 'scheduled.',
    why: 'We flag likely calls automatically, and a person confirms every one before it counts. That review can take a day. You do not need to do anything except keep the conversation going.',
    cta: 'See the conversation',
    ghost: null,
    next: 'once verified, it shows here as a call secured with the evidence attached.',
  },
  MOMENT_OUTCOME_CONFIRMED: {
    eyebrow: 'Call secured',
    // "Secured", not "booked on <date>": we detect that a call was agreed, never
    // when it is scheduled for. Claiming a time we do not have would be a lie
    // the founder plans around.
    title: 'You secured a call with',
    titleAccent: '{investorName}.',
    why: 'This is what you are here for: a call secured with a real investor, verified by a person before it counts. The charge covers securing the call; the meeting itself is yours to take. After your trial, an outcome like this is {outcomePrice}, with the evidence attached.',
    cta: 'See what happened',
    ghost: 'Question this charge',
    pill: {
      text: 'Every outcome shows its evidence. Full terms on your',
      linkLabel: 'Wallet pricing page',
    },
    next: 'the two of you set the time directly. Your profile already did the first meeting for you.',
  },
  ALERT_RECONNECT: {
    eyebrow: 'Needs you today',
    title: 'Your LinkedIn is',
    titleAccent: 'disconnected.',
    why: 'While disconnected, sending stops and this page goes quiet: replies still reach your LinkedIn inbox, but nothing here updates, drafts stop, and outcomes are not tracked. Reconnecting takes about a minute and everything picks up where it stopped.',
    cta: 'Reconnect LinkedIn',
    ghost: 'Message support',
    next: 'sending resumes, and reply tracking picks up again here.',
  },
  ALERT_SYSTEM_STALL: {
    eyebrow: 'Stopped on our side',
    title: 'Your messages stopped going out',
    titleAccent: '{daysStalled}.',
    why: 'We would rather tell you than let this page pretend everything is running. Our team is on it, you will see sending resume here, and we will tell you what happened.',
    cta: 'Message support',
    ghost: 'See send history',
    next: 'sending resumes and the pulse below starts moving again.',
  },
  ALERT_ADD_FUNDS: {
    eyebrow: 'Needs you today',
    title: 'Your free trial ended',
    titleAccent: '{daysSinceTrialEnd}.',
    why: 'Nothing about your campaign is broken. It is holding {queuedCount} approved messages and waiting on one thing. The investors you already reached are cooling off while the rest of your list has never heard from you. This takes about a minute to clear.',
    cta: 'Add funds and resume',
    ghost: 'Pause the campaign properly instead',
    pill: {
      text: 'You are only charged when something lands. Full terms on your',
      linkLabel: 'Wallet pricing page',
    },
    next: 'your {queuedCount} approved messages resume sending, gradually and safely.',
  },
  ALERT_QUEUE_DRAINED: {
    eyebrow: 'Needs you today',
    title: 'Your campaign ran out of',
    titleAccent: 'approved contacts.',
    why: 'This campaign was sending until it emptied its queue. You can approve the whole next set in one click; there is no need to go lead by lead. The low-runway warning will fire earlier next time so it does not run dry again.',
    cta: 'Approve contacts',
    ghost: 'Approve all {matchCount} at once',
    next: 'sending resumes as soon as you approve more contacts.',
  },
}

/**
 * Replaces ALERT_ADD_FUNDS's `why` when the payload sets addFundsStalledEarly.
 *
 * Sending died long before the trial ended, so "nothing about your campaign is
 * broken" would be untrue. David's decision: keep Add funds as the card, stop
 * the copy lying.
 */
export const ADD_FUNDS_STALLED_EARLY_WHY =
  'Your trial has ended, and adding funds is what resumes sending. One more thing, honestly: your campaign also stopped early in your trial, on our side. We are looking into that separately, and it will not happen on your paid balance.'

/**
 * Replaces ALERT_ADD_FUNDS's title and `why` when the payload sets
 * addFundsAfterTopUp. The founder has paid before, so the trial ending is not
 * why sending stopped — the balance dropping below the sending minimum is
 * (often an AI session charge from the same wallet).
 */
export const ADD_FUNDS_AFTER_TOP_UP_COPY = {
  title: 'Your wallet ran below',
  titleAccent: 'the sending minimum.',
  why: 'Sending pauses when your balance runs low, for example after an AI session was charged to the same wallet. Nothing about your campaign is broken. It is holding {queuedCount} approved messages, and it resumes from where it left off as soon as you add funds.',
}

export const BANNER_COPY: Record<BannerKey, { text: string; linkLabel: string; suffix: string }> = {
  BANNER_TRIAL_ENDING: {
    text: 'Your free trial ends in {daysUntilTrialEnd}.',
    linkLabel: 'Add funds',
    suffix: 'so sending does not stop.',
  },
  BANNER_LOW_RUNWAY: {
    text: '{runwayDays} of approved contacts left at your sending pace.',
    linkLabel: 'Approve more',
    suffix: 'to stay ahead.',
  },
  BANNER_REPLIES_WAITING: {
    text: '{replyCount} waiting.',
    linkLabel: 'See replies',
    suffix: '',
  },
}

/**
 * Never "today": a weekly frame absorbs every lag in the system (human outcome
 * review, HeyReach sync) rather than promising same-day accuracy we cannot hit.
 */
export const PULSE_LABEL = 'This week'
